import { useField } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import { isNil } from 'ramda'
import { useMemo } from 'react'
import { useTheme } from 'styled-components'

import { StInputErrorContainer } from '@/@bothrs-ui/web/components/forms/input/Input.styled'
import { Margin } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { Label } from '../label'
import { StFieldContainer, StInputContainer } from './Dropdown.styled'

import type { FC, SelectHTMLAttributes } from 'react'
import type { PartiallyRequired } from '@/utils/typescript'

type InputProps = {
  placeholder?: string
  label?: string
  options: { label: string; value: string }[]
  noMargin?: boolean
  ignoreTouched?: boolean
} & PartiallyRequired<SelectHTMLAttributes<HTMLSelectElement>, 'name'>

const AnimatedFieldContainer = motion(StFieldContainer)
const AnimatedFieldErrorContainer = motion(StInputErrorContainer)

const Dropdown: FC<InputProps> = ({ label, options, noMargin, ignoreTouched, ...props }) => {
  const theme = useTheme()
  const [field, meta] = useField(props)

  const hasError = useMemo(
    () => (meta.touched || ignoreTouched) && !isNil(meta.error),
    [meta.error, ignoreTouched, meta.touched],
  )

  const animatedErrorMessageProps = useMemo(
    () => ({
      initial: { opacity: 0, y: -30 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -30 },
      transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
    }),
    [],
  )

  return (
    <AnimatedFieldContainer noMargin={noMargin}>
      {label ? (
        <Label htmlFor={props.name} color={theme.Colors.Neutrals.Darker} type='BodyMediumBold'>
          {label} {props.required ? '*' : null}
        </Label>
      ) : null}
      <StInputContainer disabled={props.disabled} error={hasError}>
        <select {...props} {...field} value={props.value ?? field.value}>
          {props.placeholder ? (
            <option key='placeholder' value=''>
              {props.placeholder}
            </option>
          ) : null}

          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </StInputContainer>
      <AnimatePresence mode='popLayout'>
        {hasError && meta.error ? (
          <AnimatedFieldErrorContainer key={meta.error} {...animatedErrorMessageProps}>
            <Margin mt={theme.UI.Spacing.Space2} />
            <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
              {meta.error}
            </Span>
          </AnimatedFieldErrorContainer>
        ) : null}
      </AnimatePresence>
    </AnimatedFieldContainer>
  )
}

export { Dropdown }
